import React, { useEffect, useState } from 'react';
import { getAuth, applyActionCode, checkActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { FadeInImage } from '../common/FadeInImage';

interface AuthActionHandlerProps {
  config?: any;
}

export function AuthActionHandler({ config }: AuthActionHandlerProps) {
  const [mode, setMode] = useState<string | null>(null);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'input'>('loading');
  const [message, setMessage] = useState('Verificando...');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    const oobCodeParam = params.get('oobCode');

    setMode(modeParam);
    setOobCode(oobCodeParam);

    if (!modeParam || !oobCodeParam) {
      setStatus('error');
      setMessage('Enlace inválido o expirado.');
      return;
    }

    const auth = getAuth();

    const handleAction = async () => {
      try {
        if (modeParam === 'verifyEmail') {
          await applyActionCode(auth, oobCodeParam);
          setStatus('success');
          setMessage('¡Tu correo electrónico ha sido verificado con éxito!');
        } else if (modeParam === 'resetPassword') {
          const email = await verifyPasswordResetCode(auth, oobCodeParam);
          setEmail(email);
          setStatus('input');
          setMessage('Ingresa tu nueva contraseña');
        } else {
          setStatus('error');
          setMessage('Acción no soportada.');
        }
      } catch (error: any) {
        console.error("Auth action error:", error);
        setStatus('error');
        if (error.code === 'auth/invalid-action-code') {
          setMessage('El enlace es inválido o ya ha sido utilizado.');
        } else if (error.code === 'auth/expired-action-code') {
          setMessage('El enlace ha expirado. Por favor, solicita uno nuevo.');
        } else {
          setMessage('Ocurrió un error al procesar tu solicitud.');
        }
      }
    };

    handleAction();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setStatus('loading');
    setMessage('Actualizando contraseña...');
    const auth = getAuth();
    try {
      await confirmPasswordReset(auth, oobCode!, newPassword);
      setStatus('success');
      setMessage('¡Tu contraseña ha sido restablecida con éxito!');
    } catch (error: any) {
      console.error(error);
      setStatus('error');
      setMessage('Error al restablecer la contraseña. Intenta nuevamente.');
    }
  };

  const primaryColor = config?.primaryColor || '#eab308';

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-[100px] opacity-20 pointer-events-none"
        style={{ backgroundColor: primaryColor }}
      />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1e293b]/90 backdrop-blur-xl border border-white/10 rounded-3xl p-8 max-w-md w-full shadow-2xl relative z-10 text-center"
      >
        <div className="flex justify-center mb-6">
          {config?.logoUrl ? (
            <FadeInImage src={config.logoUrl} alt="Logo" className="w-20 h-20 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]" />
          ) : (
            <div 
               className="w-20 h-20 rounded-full flex items-center justify-center border"
               style={{ backgroundColor: `${primaryColor}15`, borderColor: `${primaryColor}30`, color: primaryColor }}
            >
               <span className="text-3xl font-black">{config?.pageName?.charAt(0) || 'R'}</span>
            </div>
          )}
        </div>

        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={40} className="animate-spin text-white/50" style={{ color: primaryColor }} />
            <h2 className="text-xl font-black uppercase tracking-wider text-white">{message}</h2>
          </div>
        )}

        {status === 'success' && (
          <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-4 py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-2">
              <CheckCircle2 size={32} className="text-emerald-400" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-wider text-white">¡Misión Cumplida!</h2>
            <p className="text-white/70 font-medium text-sm leading-relaxed">{message}</p>
            <button
              onClick={() => {
                const auth = getAuth();
                if (auth.currentUser) {
                  window.location.href = '/';
                } else {
                  window.location.href = '/?login=true';
                }
              }}
              className="mt-6 w-full text-black font-black py-4 rounded-xl uppercase tracking-widest text-sm transition-all hover:scale-105 shadow-[0_0_20px_rgba(0,0,0,0.5)]"
              style={{ backgroundColor: primaryColor }}
            >
              Ir a la App
            </button>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-4 py-6">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-2">
              <AlertCircle size={32} className="text-red-400" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-wider text-white">Algo salió mal</h2>
            <p className="text-white/70 font-medium text-sm leading-relaxed">{message}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-6 w-full bg-white/10 text-white font-black py-4 rounded-xl uppercase tracking-widest text-sm transition-all hover:bg-white/20"
            >
              Volver al inicio
            </button>
          </motion.div>
        )}

        {status === 'input' && mode === 'resetPassword' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4 w-full">
            <h2 className="text-xl font-black uppercase tracking-wider text-white">Nueva Contraseña</h2>
            <p className="text-white/60 font-medium text-xs mb-2">
              Restableciendo clave para:<br/>
              <span className="text-white font-bold">{email}</span>
            </p>
            <form onSubmit={handleResetPassword} className="w-full space-y-4">
              <div className="relative group text-left">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 opacity-40 group-focus-within:opacity-100 transition-opacity" style={{ color: primaryColor }} size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Escribe la nueva contraseña"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 focus:border-white/50 outline-none font-bold text-sm text-white transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-100 transition-opacity text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button
                type="submit"
                className="w-full text-black font-black py-4 rounded-xl uppercase tracking-widest text-sm transition-all hover:scale-105 shadow-[0_0_20px_rgba(0,0,0,0.5)] mt-4"
                style={{ backgroundColor: primaryColor }}
              >
                Actualizar Clave
              </button>
            </form>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
